import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import versionJSON from '@/js/external-js/config/version.json';
/* Init Sentry and then our app so it tracks all errors */
import * as Sentry from "@sentry/react";
import { getCurrentEnvironment } from '@/utils/featuresUtil';

const getSentryDSN = () => {
  // Default to prod DSN
  let dsn = "https://bf894d60308b3ec204aee45b8c837de3@o4506330372702208.ingest.us.sentry.io/4506697576087552";
  const env = getCurrentEnvironment();
  if (["uat", "staging"].includes(env)) {
    // UAT environment
    dsn = "https://5b746c4b18d3d62bbd42c963754fc310@o4506330372702208.ingest.us.sentry.io/4506334421516288";
  } else if (["hybrid", "development", "dev", "local", "test"].includes(env)) {
    // Hybrid/Dev environment
    dsn = "https://8881db6ea090645c46678d21e9ceb3de@o4506330372702208.ingest.us.sentry.io/4509437092888576";
  } else {
    // Production
    dsn = "https://bf894d60308b3ec204aee45b8c837de3@o4506330372702208.ingest.us.sentry.io/4506697576087552";
  }
  return dsn;
};

const environment = (window?.location?.hostname?.includes("hybrid") || window?.location?.hostname?.includes("127.0.0.1") || window?.location?.hostname?.includes("localhost") || window?.location?.hostname?.includes(".local"))
  ? "hybrid"
  : window?.location?.hostname?.includes("deit.dev")
    ? "uat"
    : "prod";

 const currentEnv = getCurrentEnvironment();
 const disabledEnvs = ["hybrid", "development", "dev", "local", "test"];
 if (!disabledEnvs.includes(currentEnv)) {
   Sentry.init({
     dsn: getSentryDSN(),
     release: `deiterate-${environment}@${versionJSON.version}`,
     integrations: [
       Sentry.browserTracingIntegration(),
       Sentry.replayIntegration()
     ],
     environment: environment,
     sendDefaultPii: true,
     tracesSampleRate: 1.0,
     replaysSessionSampleRate: 1.0,
     replaysOnErrorSampleRate: 1.0,
     tracePropagationTargets: [
       /^https:\/\/admin\.deiterate\.com/,
       /^https:\/\/login\.deiterate\.com/,
       /^https:\/\/ssr-test\.deiterate\.com/,
       /^https:\/\/ssr-test\.deit\.dev/,
       /^https:\/\/admin\.deit\.dev/,
       /^https:\/\/deiterate-hybrid\.web\.app/,
       /^https:\/\/hybrid\.deit\.dev/,
     ],
     beforeSend(event, hint) {
       if (typeof window !== "undefined") {
         Sentry.setUser({
           id: window.currentUser?.userID || "unknown",
           email: window.currentUser?.email || "unknown",
           username: window.currentUser?.firstName + ' ' + window.currentUser?.lastName || "unknown",
         });
         event.contexts = {
           ...event.contexts,
           "Company Info": {
             companyID: window.companyID || "unknown",
             companyName: window.companyName || "unknown",
           },
           user: {
             ...event.contexts?.user,
             email: window.currentUser?.email || "unknown",
             firstName: window.currentUser?.firstName || "unknown",
             lastName: window.currentUser?.lastName || "unknown",
             roles: window.currentUser?.roles || "unknown",
             team: window.currentUser?.team || "unknown",
             businessUnit: window.currentUser?.businessUnit || "unknown",
             userID: window.currentUser?.id || "unknown"
           }
         };
       }
       return event;
     },
   });
 } else {
   console.log('[Sentry] Disabled in environment:', currentEnv);
 }

import App from './app/App';

/* Styles */
import './styles/global.css';

// Auto-quiz utility for E8 questionnaire testing
// Usage: window.autoQuiz('start') | window.autoQuiz('stop') | window.autoQuiz('restart')
declare global {
  interface Window {
    autoQuiz: (command?: 'start' | 'stop' | 'restart') => void;
    __e8_autoRunner?: ReturnType<typeof setInterval>;
  }
}

window.autoQuiz = function(command: 'start' | 'stop' | 'restart' = 'start') {
  // Handle stop command
  if (command === 'stop') {
    if (window.__e8_autoRunner) {
      clearInterval(window.__e8_autoRunner);
      window.__e8_autoRunner = undefined;
      console.log('[autoQuiz] Stopped.');
    } else {
      console.log('[autoQuiz] Not running.');
    }
    return;
  }
  
  // Handle restart - stop first, then start
  if (command === 'restart') {
    if (window.__e8_autoRunner) {
      clearInterval(window.__e8_autoRunner);
      window.__e8_autoRunner = undefined;
    }
  }
  
  // Handle start (also used by restart)
  if (command === 'start' || command === 'restart') {
    // Don't start if already running (unless restart)
    if (command === 'start' && window.__e8_autoRunner) {
      console.log('[autoQuiz] Already running. Use autoQuiz("restart") to restart or autoQuiz("stop") to stop.');
      return;
    }
    
    const TICK_MS = 450;
    const UI_SETTLE_MS = 150;

    const YES_PROB_BY_LEVEL: Record<number, number> = {
      1: 0.50,
      2: 0.60,
      3: 0.55
    };

    let busy = false;

    const norm = (s: string | null) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();

    function isVisible(el: Element | null): boolean {
      if (!el) return false;
      const style = window.getComputedStyle(el);
      const rect = el.getBoundingClientRect();
      return (
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        rect.width > 0 && rect.height > 0
      );
    }

    function getVisibleRadioGroups(): Element[] {
      return Array.from(document.querySelectorAll('[role="radiogroup"]')).filter(isVisible);
    }

    function groupIsAnswered(group: Element): boolean {
      return !!group.querySelector('[role="radio"][aria-checked="true"]');
    }

    function findFirstUnansweredGroup(): Element | null {
      return getVisibleRadioGroups().find(g => !groupIsAnswered(g)) || null;
    }

    function pickOption(group: Element, yesProb: number): Element | null {
      const yes = Array.from(group.querySelectorAll('[role="radio"][value="Yes"]')).find(isVisible);
      const no = Array.from(group.querySelectorAll('[role="radio"][value="No"]')).find(isVisible);

      if (yes && no) {
        return Math.random() < yesProb ? yes : no;
      }
      if (yes) return yes;
      if (no) return no;

      const unsure = Array.from(group.querySelectorAll('[role="radio"][value="Unsure"]')).find(isVisible);
      if (unsure) return unsure;

      return Array.from(group.querySelectorAll('[role="radio"]')).find(isVisible) || null;
    }

    function clickRadio(btn: Element | null): boolean {
      if (!btn) return false;
      if (btn.getAttribute('aria-checked') !== 'true') (btn as HTMLElement).click();
      console.log('Answered:', btn.getAttribute('value') || norm(btn.textContent));
      return true;
    }

    function findVisibleSubmitButton(): HTMLButtonElement | null {
      const byText = Array.from(document.querySelectorAll('button'))
        .find(b => isVisible(b) && norm(b.textContent) === 'submit assessment');
      if (byText) return byText;

      return Array.from(document.querySelectorAll('button[name]'))
        .find(b => isVisible(b) && norm(b.getAttribute('name')) === 'submit assessment') as HTMLButtonElement || null;
    }

    function findVisibleNextButton(): HTMLButtonElement | null {
      const classBtn = Array.from(document.querySelectorAll('button.bg-primary')).find(isVisible);
      if (classBtn) return classBtn as HTMLButtonElement;

      return Array.from(document.querySelectorAll('button'))
        .find(b => isVisible(b) && norm(b.textContent) === 'next') as HTMLButtonElement || null;
    }

    function sleep(ms: number): Promise<void> {
      return new Promise(res => setTimeout(res, ms));
    }

    async function answerCascadeUpToThreeLevels(): Promise<void> {
      for (let level = 1; level <= 3; level++) {
        const group = findFirstUnansweredGroup();
        if (!group) break;

        const yesProb = YES_PROB_BY_LEVEL[level] ?? 0.5;
        const option = pickOption(group, yesProb);
        if (!option) break;

        clickRadio(option);
        await sleep(UI_SETTLE_MS);
      }
    }

    async function tick(): Promise<void> {
      if (busy) return;
      busy = true;

      try {
        const beforeUnanswered = !!findFirstUnansweredGroup();
        if (beforeUnanswered) {
          await answerCascadeUpToThreeLevels();

          if (findFirstUnansweredGroup()) {
            busy = false;
            return;
          }
        }

        const submitBtn = findVisibleSubmitButton();
        if (submitBtn) {
          submitBtn.click();
          console.log('Clicked: Submit Assessment');
          busy = false;
          return;
        }

        const nextBtn = findVisibleNextButton();
        if (nextBtn) {
          nextBtn.click();
          console.log('Clicked: Next');
        }
      } finally {
        busy = false;
      }
    }

    window.__e8_autoRunner = setInterval(() => { tick(); }, TICK_MS);
    console.log(`[autoQuiz] ${command === 'restart' ? 'Restarted' : 'Started'}. Commands: autoQuiz('stop') | autoQuiz('restart')`);
  }
};

// Create a client
const queryClient = new QueryClient();

// Define component map without explicit React.FC type
const componentMap = {
  
};

// Check if we're on a React SPA page
const isSpa = document.body.dataset.react === 'spa';

if (isSpa) {
  const spaRoot = document.getElementById('spa-root');
  if (spaRoot) {
    createRoot(spaRoot).render(
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    );
  }
} else {
  // Mount embedded widgets
  document.querySelectorAll<HTMLElement>('[data-react-component]').forEach((el) => {
    const name = el.dataset.reactComponent;
    const Component = name && componentMap[name];

    if (Component) {
      createRoot(el).render(<Component />);
    }
  });
}
